import { motion } from "framer-motion";
import { ArrowUpRight, PhoneCall, Baby, School, Sprout, BookOpen, Handshake } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { AREAS } from "./data";
import { SmartLink } from "./SmartLink";

const MotionLink = motion(SmartLink);

const ICONS = { hotline: PhoneCall, eccd: Baby, "inclusive-edu": School, "life-skills": Sprout, advocacy: BookOpen, partnerships: Handshake };

const AreaCard = ({ area, index }) => {
  const Icon = ICONS[area.id];
  const dark = index === 0 || index === 4;
  return (
    <Reveal delay={(index % 3) * 0.08} className="h-full">
      <MotionLink
        href={area.href}
        data-testid={`area-card-${area.id}`}
        whileHover={{ y: -6 }}
        transition={{ type: "spring", stiffness: 260, damping: 22 }}
        className={`group relative h-full flex flex-col rounded-[28px] p-7 sm:p-8 border overflow-hidden ${
          dark ? "bg-[#0F1D2F] text-[#FAF7F2] border-[#0F1D2F]" : "bg-white/70 text-[#0F1D2F] border-[#0F1D2F]/10"
        }`}
      >
        <span className="absolute inset-0 bg-[#CF5C0F] translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out" />
        <div className="relative flex items-start justify-between">
          <span className={`w-14 h-14 rounded-2xl flex items-center justify-center ${dark ? "bg-[#CF5C0F]" : "bg-[#F6E3D3] text-[#B34C0A]"} group-hover:bg-white group-hover:text-[#CF5C0F] transition-colors`}>
            <Icon className="w-6 h-6" />
          </span>
          <span className="font-mono text-sm opacity-70 group-hover:text-white">0{index + 1}</span>
        </div>
        <h3 className="relative font-serif text-2xl sm:text-[1.75rem] leading-tight mt-10 group-hover:text-white transition-colors">{area.title}</h3>
        <p className={`relative mt-4 text-sm sm:text-base leading-relaxed flex-1 ${dark ? "text-[#CBD5E1]" : "text-[#3A4756]"} group-hover:text-white/90 transition-colors`}>
          {area.text}
        </p>
        <span className="relative mt-8 inline-flex items-center gap-2 text-sm font-semibold group-hover:text-white">
          Read More <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
        </span>
      </MotionLink>
    </Reveal>
  );
};

export const AreasGrid = () => (
  <div data-testid="areas-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6 md:auto-rows-fr">
    {AREAS.map((a, i) => (
      <AreaCard key={a.id} area={a} index={i} />
    ))}
  </div>
);

export const Areas = () => (
  <section id="about" className="relative bg-[#F3EADF] py-24 lg:py-36 overflow-hidden">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <div className="grid lg:grid-cols-12 gap-10 items-center mb-20">
        <div className="lg:col-span-6">
          <Eyebrow testId="mission-eyebrow">Our Approach</Eyebrow>
          <Reveal>
            <h2 data-testid="mission-heading" className="font-serif text-5xl sm:text-6xl lg:text-7xl text-[#0F1D2F] mt-6 leading-[0.95] tracking-tight">
              On a Mission to Help. <em className="text-fire">The Right Way.</em>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 text-base sm:text-lg text-[#2B3A4A] leading-relaxed max-w-xl">
              Wings of Fire Foundation has focused on strategies of community development and the inclusion of children and persons with disabilities through the model of Community Based Rehabilitation (CBR).
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <SmartLink href="/about-us" data-testid="about-us-button" className="btn-dark mt-10">
              About Us <ArrowUpRight className="w-4 h-4" />
            </SmartLink>
          </Reveal>
        </div>
        <Reveal delay={0.15} className="lg:col-span-5 lg:col-start-8">
          <div className="relative">
            <div className="rounded-[200px_200px_28px_28px] overflow-hidden aspect-[4/5]">
              <img src="/images/p9.jpg" alt="Children in a classroom session" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-4 sm:-left-10 bg-[#CF5C0F] text-white rounded-3xl px-6 py-5 shadow-xl">
              <p className="font-serif text-4xl">CBR</p>
              <p className="text-xs font-mono tracking-widest uppercase">Community Based Rehabilitation</p>
            </div>
          </div>
        </Reveal>
      </div>

      <div id="areas" className="pt-6">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <Reveal>
            <h2 data-testid="areas-heading" className="font-serif text-4xl sm:text-5xl text-[#0F1D2F]">Our Areas of Work</h2>
          </Reveal>
          <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#B34C0A]">Six pillars · One mission</p>
        </div>
        <AreasGrid />
      </div>
    </div>
  </section>
);
